import React, { useEffect, useState } from 'react'
import { proxy, useSnapshot } from 'valtio'

import { TtButton, TtCollapsable, TtIcon } from '@23/react'

import './Reply.scss'
import { intl } from 'lang/intl'
import { useChatMessage } from '../../blocs/useChat'

const replyState = proxy<{ selected: number | null }>({
  selected: null,
})

const selectReplyComment = (id: number | null) => {
  replyState.selected = replyState.selected !== id ? id : null
}

const useActiveReplyComment = () => {
  return useSnapshot(replyState).selected
}

interface ReplyProps {
  commentId: number
}

const Reply = ({ commentId }: ReplyProps) => {
  const comment = useChatMessage(commentId)

  const replyToName = `${comment?.fullName || comment?.name} ${
    comment?.fullName && comment.fullName !== comment.name
      ? intl.formatMessage(
          {
            defaultMessage: '(shown as {name})',
          },
          { name: comment.name }
        )
      : ''
  }`

  return (
    <div className="chat-reply">
      <div className="chat-reply-header">
        <div className="chat-reply-user">
          {comment && (
            <>
              <span>Mention {replyToName}</span>
              {comment.reply?.label && (
                <span className="chat-reply-user-label">{comment.label}</span>
              )}
            </>
          )}
        </div>

        <TtButton
          className="chat-reply-close"
          variant="ghost"
          color="secondary"
          circle
          size="x-small"
          onClick={() => selectReplyComment(null)}
        >
          <TtIcon library="one" name="close" />
        </TtButton>
      </div>
      <div className="chat-reply-message">{comment?.textContent}</div>
    </div>
  )
}

const CollapsingReply = () => {
  const selectedCommentId = useActiveReplyComment() ?? undefined
  const [heldCommentId, setHeldComment] = useState(selectedCommentId)

  useEffect(() => {
    if (selectedCommentId) setHeldComment(selectedCommentId)
  }, [selectedCommentId])

  // saves one render cycle from using an undefined `heldCommentId`
  const fallbackCommentId = heldCommentId || selectedCommentId

  return (
    <TtCollapsable
      open={!!selectedCommentId}
      onTt-after-hide={() => {
        selectReplyComment(null)
      }}
    >
      {fallbackCommentId && <Reply commentId={fallbackCommentId} />}
    </TtCollapsable>
  )
}

export { CollapsingReply as Reply, selectReplyComment, useActiveReplyComment }
